// PostCard.js - Updated version
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShare, 
  faEdit, 
  faTrash, 
  faVideo, 
  faMusic, 
  faImage, 
  faLink,
  faChevronLeft,
  faChevronRight,
  faPlay,
  faPause,
  faVolumeMute,
  faVolumeUp,
  faTimes,
  faImages,
  faEllipsisV,
  faFlag
} from '@fortawesome/free-solid-svg-icons';
import UserContext from '../../context/UserContext';
import styles from './PostCard.module.css';
import ShareModal from './ShareModal';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';

const formatPostDate = (timestamp) => {
  // Existing function code
  if (!timestamp) return 'Date unavailable';
  
  // Handle both Firestore Timestamp and regular Date objects
  const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'Date unavailable';
  
  // Format as DD/MM/YYYY
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatAudioTime = (seconds) => {
  // Existing function code
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const PostCard = ({ 
  post, 
  isOwner, 
  onDelete, 
  onEdit, 
  showHeader = true,
  className = '',
  isMobile = false,
  isPreview = false,
  onOpenPost = null,
  registerVideoRef = null,
  registerAudioRef = null,
  onReportPost = null
}) => {
  const { getUserDataFromCache } = useContext(UserContext);
  const [imageError, setImageError] = useState(false);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [authorData, setAuthorData] = useState(null);
  const isGalleryModal = className.includes('post-gallery-modal');

  const navigate = useNavigate();
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const audioProgressRef = useRef(null);
  const pendingPlayPromiseRef = useRef(null);
  const pendingVideoPlayPromiseRef = useRef(null);

  // For preview mode, we use the thumbnail instead of the full media
  const mediaUrl = isPreview && post.thumbnailUrl ? post.thumbnailUrl : post.url;

   // Fetch user data of post author
   useEffect(() => {
    const fetchAuthorData = async () => {
      if (post.userId && !isPreview) {
        try {
          const userData = await getUserDataFromCache(post.userId);
          setAuthorData(userData);
        } catch (error) {
          console.error('Error fetching author data:', error);
        }
      }
    };

    fetchAuthorData();
  }, [post.userId, getUserDataFromCache, isPreview]);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMenu) {
        setShowMenu(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMenu]);
  
 
 
  useEffect(() => {
    if (videoRef.current && registerVideoRef && post.type === 'video') {
      registerVideoRef(videoRef.current);
    }
  }, [videoRef.current, registerVideoRef, post.type]);
  
  // Autoplay when in view (for videos in full view mode)
 // In PostCard.js, update the observation cleanup:
useEffect(() => {
  if (!isPreview && videoRef.current && post.type === 'video') {
    let playbackPosition = 0;
    let observer;
    
    const handleIntersection = (entries) => {
      const [entry] = entries;
      
      if (entry.isIntersecting) {
        // ... existing code
      } else {
        // ... existing code
      }
    };
    
    observer = new IntersectionObserver(handleIntersection, { threshold: 0.5 });
    
    const elementToObserve = videoRef.current.closest('.post-video') || videoRef.current.parentElement;
    if (elementToObserve) {
      observer.observe(elementToObserve);
    }
    
    return () => {
      if (observer && elementToObserve) {
        observer.unobserve(elementToObserve);
        observer.disconnect();
      }
    };
  }
}, [isPreview, post.type]);
  
  

 useEffect(() => {
    if (audioRef.current && registerAudioRef && post.type === 'audio') {
      registerAudioRef(audioRef.current);
    }
  }, [audioRef.current, registerAudioRef, post.type]);
  
  // Update audio progress
  useEffect(() => {
    if (audioRef.current) {
      const updateTime = () => {
        if (audioRef.current) {  // Add null check here
          setAudioCurrentTime(audioRef.current.currentTime);
        }
      };
      
      const handleDurationChange = () => {
        if (audioRef.current) {  // Add null check here
          setAudioDuration(audioRef.current.duration || 0);
        }
      };
      
      const handleEnded = () => {
        if (audioRef.current) {  // Add null check here
          setIsAudioPlaying(false);
          setAudioCurrentTime(0);
          audioRef.current.currentTime = 0;
        }
      };
      
      audioRef.current.addEventListener('timeupdate', updateTime);
      audioRef.current.addEventListener('durationchange', handleDurationChange);
      audioRef.current.addEventListener('ended', handleEnded);
      
      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('timeupdate', updateTime);
          audioRef.current.removeEventListener('durationchange', handleDurationChange);
          audioRef.current.removeEventListener('ended', handleEnded);
        }
      };
    }
  }, [audioRef.current]);
  
  // Add this new effect for scroll autoplay/pause for audio similar to videos
  useEffect(() => {
    if (!isPreview && audioRef.current && post.type === 'audio') {
      let playbackPosition = 0;
      let wasPlaying = false;
      
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          
          if (entry.isIntersecting) {
            // Audio is visible, resume playing if it was playing before
            if (wasPlaying && audioRef.current) {
              audioRef.current.currentTime = playbackPosition;
              
              // Similar promise handling as in toggleAudioPlay
              const playPromise = audioRef.current.play();
              pendingPlayPromiseRef.current = playPromise;
              
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    setIsAudioPlaying(true);
                    pendingPlayPromiseRef.current = null;
                  })
                  .catch(err => {
                    console.log("Audio autoplay prevented:", err);
                    pendingPlayPromiseRef.current = null;
                  });
              }
            }
          } else {
            // Audio is not visible, pause it
            if (audioRef.current) {
              wasPlaying = !audioRef.current.paused;
              
              if (!audioRef.current.paused) {
                playbackPosition = audioRef.current.currentTime;
                
                // Check for pending play promise before pausing
                if (pendingPlayPromiseRef.current) {
                  pendingPlayPromiseRef.current
                    .then(() => {
                      audioRef.current?.pause();
                      setIsAudioPlaying(false);
                      pendingPlayPromiseRef.current = null;
                    })
                    .catch(() => {
                      pendingPlayPromiseRef.current = null;
                    });
                } else {
                  audioRef.current.pause();
                  setIsAudioPlaying(false);
                }
              }
            }
          }
        },
        { threshold: 0.5 }
      );
      
      const elementToObserve = audioRef.current.closest('.post-audio') || 
                             audioRef.current.parentElement?.parentElement;
      
      if (elementToObserve) {
        observer.observe(elementToObserve);
      }
      
      return () => {
        if (elementToObserve) {
          observer.unobserve(elementToObserve);
        }
      };
    }
  }, [isPreview, post.type, audioRef.current]);
  
  // Video play/pause toggle
  const togglePlay = (e) => {
    // Existing function code
    e.stopPropagation();
    if (!videoRef.current) return;
    
    if (videoRef.current.paused) {
      // Start playing
      const playPromise = videoRef.current.play();
      
      // Store the play promise
      pendingVideoPlayPromiseRef.current = playPromise;
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            pendingVideoPlayPromiseRef.current = null; // Clear the promise reference
          })
          .catch(err => {
            console.error("Error playing video:", err);
            setIsPlaying(false);
            pendingVideoPlayPromiseRef.current = null; // Clear the promise reference
          });
      }
    } else {
      // Check if we need to wait for a play promise to resolve
      if (pendingVideoPlayPromiseRef.current) {
        pendingVideoPlayPromiseRef.current
          .then(() => {
            videoRef.current?.pause();
            setIsPlaying(false);
            pendingVideoPlayPromiseRef.current = null;
          })
          .catch(() => {
            pendingVideoPlayPromiseRef.current = null;
          });
      } else {
        // No pending play promise, safe to pause
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };
  
   // Video mute/unmute toggle
   const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };
  
  // Audio play/pause toggle
  const toggleAudioPlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    
    if (audioRef.current.paused) {
      // Start playing
      const playPromise = audioRef.current.play();
      
      // Store the play promise
      pendingPlayPromiseRef.current = playPromise;
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsAudioPlaying(true);
            pendingPlayPromiseRef.current = null; // Clear the promise reference
          })
          .catch(err => {
            console.error("Error playing audio:", err);
            setIsAudioPlaying(false);
            pendingPlayPromiseRef.current = null; // Clear the promise reference
          });
      }
    } else {
      // Check if we need to wait for a play promise to resolve
      if (pendingPlayPromiseRef.current) {
        pendingPlayPromiseRef.current
          .then(() => {
            audioRef.current.pause();
            setIsAudioPlaying(false);
            pendingPlayPromiseRef.current = null;
          })
          .catch(() => {
            pendingPlayPromiseRef.current = null;
          });
      } else {
        // No pending play promise, safe to pause
        audioRef.current.pause();
        setIsAudioPlaying(false);
      }
    }
  };
  
  
  // Audio seek functionality
  const handleAudioSeek = (e) => {
    if (audioRef.current && audioProgressRef.current) {
      const rect = audioProgressRef.current.getBoundingClientRect();
      const position = (e.clientX - rect.left) / rect.width;
      const seekTime = position * (audioRef.current.duration || 0); // Add fallback value
      
      if (!isNaN(seekTime)) { // Make sure seekTime is a valid number
        audioRef.current.currentTime = seekTime;
        setAudioCurrentTime(seekTime);
      }
    }
  };
  
  // Handle carousel navigation
  const navigateCarousel = (e, direction) => {
    // Existing function code
    e.stopPropagation();
    
    if (!post.isCarousel || !post.carousel) return;
    
    if (direction === 'next') {
      setCurrentCarouselIndex(prev => 
        prev < post.carousel.length - 1 ? prev + 1 : 0
      );
    } else {
      setCurrentCarouselIndex(prev => 
        prev > 0 ? prev - 1 : post.carousel.length - 1
      );
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleShare = async (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
  
    const shareData = {
      title: `Check out this ${post.type} on OnTour`,
      text: post.caption || 'Check out this post',
      url: `${window.location.origin}/p/${post.postId}`
    };
  
    // Add hashtags if present
    if (post.tags?.length > 0) {
      const hashtags = post.tags.map(tag => `#${tag}`).join(' ');
      shareData.text = `${shareData.text}\n\n${hashtags}`;
    }
  
    // Check if it's mobile and has native sharing
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobileDevice && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          setShowShareModal(true); // Fallback to modal if native share fails
        }
      }
    } else {
      // On desktop, always show the ShareModal
      setShowShareModal(true);
    }
  };

  const handleEditClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    onEdit?.(post);
    setShowMenu(false);
  };

  const handleDeleteClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    onDelete?.(post.postId);
    setShowMenu(false);
  };
  
  const handleReportClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    onReportPost?.(post);
    setShowMenu(false);
  };

  const handlePostClick = () => {
    if (isPreview && onOpenPost) {
      onOpenPost(post);
    } else if (!isPreview && !onOpenPost) {
      navigate(`/p/${post.postId}`);
    }
  };
  
  const handleUserClick = (e) => {
    e.stopPropagation();
    navigate(`/${post.userName}`);
  };
  
  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };
  
  // Render the post header with profile pic, username, share and menu
  const renderPostHeader = (isOverlay = false) => {
    if (isPreview) return null;
    
    return (
      <div className={`${styles["post-header"]} ${isOverlay ? styles["overlay"] : ''}`}>
        <div className={styles["post-header-left"]} onClick={handleUserClick}>
          <img 
            src={authorData?.profilePictureUrl || defaultProfilePhoto} 
            alt={post.userName || 'User'} 
            className={styles["user-avatar"]}
          />
          <span className={styles["user-name"]}>{post.userName || 'User'}</span>
        </div>
        <div className={styles["post-header-right"]}>
          <button 
            onClick={handleShare} 
            className={styles["header-action-btn"]} 
            title="Share post"
          >
            <FontAwesomeIcon icon={faShare} />
          </button>
          <div className={styles["menu-container"]}>
            <button 
              onClick={handleMenuClick} 
              className={styles["header-action-btn"]} 
              title="More options"
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>
            {showMenu && (
              <div className={styles["menu-dropdown"]} onClick={e => e.stopPropagation()}>
                {isOwner ? (
                  <>
                    <button 
                      onClick={handleEditClick} 
                      className={styles["menu-item"]}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Edit Post</span>
                    </button>
                    <button 
                      onClick={handleDeleteClick} 
                      className={styles["menu-item"]}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Delete Post</span>
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={handleReportClick} 
                    className={styles["menu-item"]}
                  >
                    <FontAwesomeIcon icon={faFlag} />
                    <span>Report Post</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  
  
  // Render the type indicator icon for preview mode
  const renderTypeIndicator = () => {
    if (!isPreview) return null;
    
    let icon = faImage; // Default to image icon
    let className = styles["post-type-indicator"];
    
    if (post.type === 'video') {
      icon = faVideo;
    } else if (post.type === 'audio') {
      icon = faMusic;
    } else if (post.isCarousel) {
      icon = faImages;
      className += " " + styles["post-carousel-indicator"];
    }
    
    return (
      <div className={className}>
        <FontAwesomeIcon icon={icon} size="xs" />
      </div>
    );
  };

 const renderContent = () => {
    if (imageError) {
      return (
        <div className={styles["post-fallback"]}>
          <div className={styles["fallback-content"]}>
            <FontAwesomeIcon 
              icon={post.type === 'video' ? faVideo : post.type === 'audio' ? faMusic : faImage} 
              className={styles["media-icon"]} 
            />
            <span>Media unavailable</span>
          </div>
        </div>
      );
    }

    // If this is an uploaded file (direct media), render it differently
    if (post.isUploaded) {
      // For carousel posts
      if (post.isCarousel && post.carousel) {
        const currentItem = getCurrentCarouselItem();
        
        return (
          <div 
            className={styles["post-carousel"]}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            <img 
              src={currentItem.url} 
              alt={post.caption || 'Carousel image'}
              className={styles["carousel-image"]}
              onError={handleImageError}
            />
            
            {post.carousel.length > 1 && (showControls || isMobile) && (
              <>
                <button 
                  className={`${styles["carousel-nav"]} ${styles["prev"]}`}
                  onClick={(e) => navigateCarousel(e, 'prev')}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button 
                  className={`${styles["carousel-nav"]} ${styles["next"]}`}
                  onClick={(e) => navigateCarousel(e, 'next')}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                <div className={styles["carousel-indicators"]}>
                  {post.carousel.map((_, index) => (
                    <div 
                      key={index} 
                      className={`${styles["indicator"]} ${index === currentCarouselIndex ? styles["active"] : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentCarouselIndex(index);
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      }
      
      // For video files
      if (post.type === 'video' && post.contentType?.startsWith('video/')) {
        if (isPreview) {
          // In preview mode, show thumbnail with play button overlay
          return (
            <div className={`${styles["post-video"]} ${styles["preview"]}`}>
              <img 
                src={post.thumbnailUrl || post.url} 
                alt={post.caption || 'Video thumbnail'}
                className={styles["video-thumbnail"]}
                onError={handleImageError}
              />
              <div className={styles["play-overlay"]}>
                <FontAwesomeIcon icon={faPlay} />
              </div>
            </div>
          );
        }
        
        // Check if this is a vertical video (9:16 aspect ratio)
        const isVertical = post.videoOrientation === 'portrait' || 
                          (post.videoMetadata?.width && post.videoMetadata?.height && 
                           post.videoMetadata.height > post.videoMetadata.width * 1.5);
        
        return (
          <div 
            className={`${styles["post-video"]} ${isVertical ? styles["vertical-video"] : ''}`}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            {renderPostHeader(true)}
            <video 
              ref={videoRef}
              src={post.url} 
              poster={post.thumbnailUrl}
              className={`${styles["media-file"]} ${isVertical ? styles["vertical"] : ''}`}
              onError={handleImageError}
              onClick={togglePlay}
              playsInline
              loop
              muted={isMuted}
            />
            
            {!isPlaying && (
              <div className={styles["play-overlay"]} onClick={togglePlay}>
                <FontAwesomeIcon icon={faPlay} />
              </div>
            )}
            
            {(showControls || isMobile) && (
              <div className={styles["video-controls"]}>
                <button className={styles["video-control-btn"]} onClick={togglePlay}>
                  <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                </button>
                <button className={styles["video-control-btn"]} onClick={toggleMute}>
                  <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
                </button>
              </div>
            )}
          </div>
        );
      }
      
      
      // For audio files
      else if (post.type === 'audio' && post.contentType?.startsWith('audio/')) {
        if (isPreview) {
          // In preview mode, show cover image with audio icon
          return (
            <div className={`${styles["post-audio"]} ${styles["preview"]}`}>
              <div className={styles["audio-container"]}>
                {post.thumbnailUrl ? (
                  <img 
                    src={post.thumbnailUrl} 
                    alt={post.caption || 'Audio cover'} 
                    className={styles["audio-cover"]}
                    onError={handleImageError}
                  />
                ) : (
                  <div className={styles["audio-visual"]}>
                    <FontAwesomeIcon icon={faMusic} className={styles["audio-icon"]} />
                  </div>
                )}
              </div>
            </div>
          );
        }
        
        return (
          <div className={styles["post-audio"]}>
            <div className={styles["audio-container"]}>
              {post.thumbnailUrl ? (
                <img 
                  src={post.thumbnailUrl} 
                  alt={post.caption || 'Audio cover'} 
                  className={styles["audio-cover"]}
                  onError={handleImageError}
                />
              ) : (
                <div className={styles["audio-visual"]}>
                  <FontAwesomeIcon icon={faMusic} className={styles["audio-icon"]} />
                </div>
              )}
              
              <div className={styles["audio-player"]}>
                <audio 
                  ref={audioRef}
                  src={post.url} 
                  onError={handleImageError}
                  preload="metadata"
                />
                
                <div className={styles["audio-controls"]}>
                  <button className={styles["audio-play-btn"]} onClick={toggleAudioPlay}>
                    <FontAwesomeIcon icon={isAudioPlaying ? faPause : faPlay} />
                  </button>
                  
                  <div 
                    className={styles["audio-progress"]} 
                    ref={audioProgressRef}
                    onClick={handleAudioSeek}
                  >
                    <div 
                      className={styles["audio-progress-bar"]} 
                      style={{ width: `${(audioCurrentTime / Math.max(audioDuration, 1)) * 100}%` }}
                    ></div>
                    <div 
                      className={styles["audio-progress-thumb"]} 
                      style={{ left: `${(audioCurrentTime / Math.max(audioDuration, 1)) * 100}%` }}
                    ></div>
                  </div>
                  
                  <div className={styles["audio-time"]}>
                    {formatAudioTime(audioCurrentTime)} / {formatAudioTime(audioDuration)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      // For image files
      else if (post.type === 'photo' && post.contentType?.startsWith('image/')) {
        return (
          <div className={styles["post-photo"]}>
            <img 
              src={mediaUrl} 
              alt={post.caption || 'User uploaded image'} 
              className={styles["media-file"]}
              onError={handleImageError}
            />
          </div>
        );
      }
    }

    // For audio embeds
    if (post.type === 'audio' && post.isEmbed) {
    
      if (isPreview) {
        // In preview mode, show thumbnail
        return (
          <div className={`${styles["post-audio-embed"]} ${styles["preview"]}`}>
            <img 
              src={post.thumbnailUrl} 
              alt={post.title || 'Audio thumbnail'}
              className={styles["audio-thumbnail"]}
              onError={handleImageError}
            />
           
          </div>
        );
      }
      
      return (
        <div className={styles["post-audio-embed"]}>
          <div 
            className={styles["embed-player"]}
            dangerouslySetInnerHTML={{ __html: post.embedHtml }}
          />
        </div>
      );
    }

    return (
      <div className={styles["post-fallback"]}>
        <div className={styles["fallback-content"]}>
          <FontAwesomeIcon 
            icon={post.type === 'video' ? faVideo : post.type === 'audio' ? faMusic : faImage} 
            className={styles["media-icon"]} 
          />
          <span>Media unavailable</span>
        </div>
      </div>
    );
  };
  
  // Get current carousel item
  const getCurrentCarouselItem = () => {
    if (post.isCarousel && post.carousel && post.carousel[currentCarouselIndex]) {
      return {
        url: isPreview ? post.carousel[currentCarouselIndex].thumbnailUrl || post.carousel[currentCarouselIndex].url : post.carousel[currentCarouselIndex].url,
        contentType: post.carousel[currentCarouselIndex].contentType
      };
    }
    return { url: mediaUrl, contentType: post.contentType };
  };


  return (
    <div className={`${styles["post-card"]} ${className} ${isPreview ? styles["preview-mode"] : ''} ${isGalleryModal ? styles["post-gallery-modal"] : ''}`}>
      {isGalleryModal ? (
        isMobile ? (
          // Mobile gallery layout
          <div className={styles["mobile-content-container"]}>
            <div className={styles["mobile-media-wrapper"]}>
              {renderPostHeader(true)}
              {renderContent()}
            </div>
            {!isPreview && (
              <div className={styles["mobile-info-overlay"]}>
                <p className={styles["post-caption"]}>
                  <span className={styles["caption-username"]} onClick={handleUserClick}>
                    {post.userName || 'User'}
                  </span> 
                  {post.caption}
                </p>
                <div className={styles["post-tags"]}>
                  {post.tags?.map((tag, index) => (
                    <span key={index} className={styles["tag"]}>#{tag}</span>
                  ))}
                </div>
                <div className={styles["post-date"]}>
                  {formatPostDate(post.createdAt)}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Desktop gallery layout (Instagram-like split)
          <>
            <div className={styles["desktop-content-container"]}>
              {renderPostHeader(true)}
              <div className={styles["media-wrapper"]}>
                {renderContent()}
              </div>
            </div>
            {!isPreview && (
              <div className={styles["desktop-info-container"]}>
                {renderPostHeader(false)}
                <div className={styles["post-info-section"]}>
                  <p className={styles["post-caption"]}>
                    <span className={styles["caption-username"]} onClick={handleUserClick}>
                      {post.userName || 'User'}
                    </span> 
                    {post.caption}
                  </p>
                  <div className={styles["post-tags"]}>
                    {post.tags?.map((tag, index) => (
                      <span key={index} className={styles["tag"]}>#{tag}</span>
                    ))}
                  </div>
                  <div className={styles["post-date"]}>
                    {formatPostDate(post.createdAt)}
                  </div>
                </div>
              </div>
            )}
          </>
        )
      ) : (
        // Regular preview mode
        <>
          {renderTypeIndicator()}
          
          {/* Render header for non-video posts outside the content */}
          {!isPreview && post.type !== 'video' && renderPostHeader()}
          
          <div onClick={handlePostClick} className={styles["post-content"]}>
            {renderContent()}
          </div>
          
          {!isPreview && (
            <div className={styles["post-info"]}>
              <p className={styles["post-caption"]}>
                <span className={styles["caption-username"]} onClick={handleUserClick}>
                  {post.userName || 'User'}
                </span> 
                {post.caption}
              </p>
              <div className={styles["post-tags"]}>
                {post.tags?.map((tag, index) => (
                  <span key={index} className={styles["tag"]}>#{tag}</span>
                ))}
              </div>
              <div className={styles["post-date"]}>
                {formatPostDate(post.createdAt)}
              </div>
            </div>
          )}
          {showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          postData={post}
        />
      )}
        </>
      )}
    </div>
  );
};

export default PostCard;